import React, { useMemo, useState } from 'react'
import {
  WorkingDays,
  WORKING_DAY_STATE_LABELS,
  WORKING_DAY_STATES,
  WORKING_DAYS_WEEKDAY_VALUE,
  type WorkingDayKey,
  type WorkingDayState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './WorkingDaysShowcase.css'

const WorkingDaysShowcase: React.FC = () => {
  const [value, setValue] = useState<WorkingDayKey[]>(
    WORKING_DAYS_WEEKDAY_VALUE,
  )
  const [interactive, setInteractive] = useState(true)
  const [highlightedState, setHighlightedState] =
    useState<WorkingDayState>('available')
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'working-days-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="working-days-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(event.target.value as WorkingDayState)
                }
              >
                {WORKING_DAY_STATES.map((option) => (
                  <option key={option} value={option}>
                    {WORKING_DAY_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'working-days-interactive',
            label: 'Interactive',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="working-days-interactive"
                  type="checkbox"
                  checked={interactive}
                  onChange={(event) => setInteractive(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'working-days-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="working-days-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedState, interactive],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Working days"
        description="Week strip of M–S indicators for provider availability, as used in Provider Management."
        stageClassName="ui-preview__stage--wide working-days-showcase__stage-wrap"
      >
        <div
          className={`working-days-showcase__sheet${
            darkMode ? ' working-days-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {interactive
                ? 'Click a day to toggle availability.'
                : 'Display-only strip (table style).'}
            </p>
            <WorkingDays
              value={value}
              interactive={interactive}
              darkMode={darkMode}
              onChange={setValue}
            />
          </section>

          <section className="working-days-showcase__matrix">
            <h4 className="working-days-showcase__section-title">States</h4>
            {WORKING_DAY_STATES.map((state) => (
              <div
                key={state}
                className={`working-days-showcase__row${
                  state === highlightedState
                    ? ' working-days-showcase__row--selected'
                    : ''
                }${darkMode ? ' working-days-showcase__row--dark' : ''}`}
              >
                <span className="working-days-showcase__row-label">
                  {WORKING_DAY_STATE_LABELS[state]}
                </span>
                <WorkingDays previewState={state} darkMode={darkMode} />
              </div>
            ))}
          </section>

          <section className="working-days-showcase__matrix">
            <h4 className="working-days-showcase__section-title">Examples</h4>
            <div
              className={`working-days-showcase__row${
                darkMode ? ' working-days-showcase__row--dark' : ''
              }`}
            >
              <span className="working-days-showcase__row-label">Weekdays</span>
              <WorkingDays
                previewValue={WORKING_DAYS_WEEKDAY_VALUE}
                darkMode={darkMode}
              />
            </div>
            <div
              className={`working-days-showcase__row${
                darkMode ? ' working-days-showcase__row--dark' : ''
              }`}
            >
              <span className="working-days-showcase__row-label">
                Mixed week
              </span>
              <WorkingDays
                previewValue={['mon', 'wed', 'fri', 'sat']}
                darkMode={darkMode}
              />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Working days"
        sections={propertySections}
      />
    </>
  )
}

export default WorkingDaysShowcase
